import {
  GeneratorKey,
  leadGenerationCampaignsRetrieveOptions,
  leadGenerationCampaignsRunsListQueryKey,
  leadGenerationCampaignsTriggerCreateMutation
} from '@crm/api'
import { AppLink } from '@crm/components'
import {
  CampaignRuns,
  CampaignSettingsCard,
  CampaignStatusTag,
  GeneratorTag
} from '@crm/features/lead-generation/components'
import { paths } from '@crm/routes'
import { TitleSection } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Empty, Flex, notification } from 'antd'
import { ArrowLeft2, Play } from 'iconsax-react'
import { useParams } from 'react-router-dom'

const isGeneratorKey = (value?: string): value is GeneratorKey =>
  !!value && Object.values(GeneratorKey).includes(value as GeneratorKey)

export const LeadGenerationCampaign = () => {
  const { t } = useLocales()
  const { generatorKey } = useParams()

  const { invalidateQueries } = useInvalidatePredicateQueries()

  const isKnownGenerator = isGeneratorKey(generatorKey)

  const { data: campaign, isLoading } = useQuery({
    ...leadGenerationCampaignsRetrieveOptions({
      path: { generator_key: generatorKey as GeneratorKey }
    }),
    enabled: isKnownGenerator
  })

  const { mutate: triggerRun, isPending } = useMutation({
    ...leadGenerationCampaignsTriggerCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        leadGenerationCampaignsRunsListQueryKey({
          path: { generator_key: generatorKey as GeneratorKey }
        })
      ])

      notification.success({
        message: t('success.success'),
        description: t('lead_generation.run_triggered')
      })
    }
  })

  const onTrigger = () => {
    if (isKnownGenerator) {
      triggerRun({ path: { generator_key: generatorKey } })
    }
  }

  const backLink = (
    <AppLink to={paths.leadGeneration.root}>
      <Flex align="center" gap={4} className="mb-16">
        <ArrowLeft2 size={16} />
        {t('lead_generation.back_to_lead_generation')}
      </Flex>
    </AppLink>
  )

  if (!isKnownGenerator) {
    return (
      <>
        {backLink}
        <Empty description={t('lead_generation.unknown_generator')} />
      </>
    )
  }

  return (
    <>
      {backLink}

      <TitleSection
        title={campaign?.name ?? ''}
        isLoading={isLoading}
        extraTitle={
          <Flex align="center" gap={8} wrap>
            <GeneratorTag generatorKey={generatorKey} />
            <CampaignStatusTag isActive={campaign?.is_active} />
          </Flex>
        }
      >
        <Button
          type="primary"
          icon={<Play size={18} />}
          loading={isPending}
          onClick={onTrigger}
        >
          {t('lead_generation.run_now')}
        </Button>
      </TitleSection>

      <Flex vertical gap={18}>
        <CampaignSettingsCard
          campaign={campaign}
          generatorKey={generatorKey}
          isLoading={isLoading}
        />

        <CampaignRuns generatorKey={generatorKey} />
      </Flex>
    </>
  )
}
